"use strict";
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildSaveChangeHistory = exports.isStrategiesConfigurationResponse = exports.isStrategy = exports.isStrategyColumnArray = exports.isStrategyColumn = exports.isStrategyNode = exports.stableStringify = exports.truncateForDisplay = exports.sanitizeForLog = exports.safeDeleteEdge = exports.safeWriteEdge = exports.RESERVED_OBJECT_KEYS = void 0;
// ─── Security helpers ─────────────────────────────────────────────
//
// Co-located with the change-tracking utilities so every editor-side
// boundary helper lives in one place. Each helper is independently testable
// and pure (no React / DOM dependencies).
/**
 * Object keys that, when assigned via bracket access, can mutate the
 * prototype chain of the host object (e.g. `obj["__proto__"] = X`). Any
 * write that originates from server-supplied data is screened against
 * this set as defense in depth against prototype pollution (CWE-1321).
 */
exports.RESERVED_OBJECT_KEYS = new Set([
    '__proto__',
    'prototype',
    'constructor',
]);
/**
 * Set or update an outgoing-edge weight on a strategy node without
 * exposing the surrounding object to prototype pollution. Server-supplied
 * node ids end up as keys in the returned record; using a null-prototype
 * object plus a reserved-key reject list guarantees an id of `__proto__`,
 * `constructor` or `prototype` cannot mutate the live `Object.prototype`.
 */
exports.safeWriteEdge = function (edges, targetId, weight) {
    if (exports.RESERVED_OBJECT_KEYS.has(targetId)) {
        return edges;
    }
    var next = Object.assign(Object.create(null), edges);
    next[targetId] = weight;
    return next;
};
/**
 * Remove an outgoing-edge entry from a strategy node, returning a new
 * null-prototype record. Reserved-key writes are no-ops by construction
 * (the key cannot exist on a sanitized record) but the helper still
 * rejects them defensively.
 */
exports.safeDeleteEdge = function (edges, targetId) {
    if (exports.RESERVED_OBJECT_KEYS.has(targetId)) {
        return edges;
    }
    var next = Object.assign(Object.create(null), edges);
    delete next[targetId];
    return next;
};
/**
 * Strip CR/LF/TAB and other C0 control characters and clamp length so
 * user-supplied names cannot inject fake log entries or smuggle terminal
 * control sequences (CWE-117 — log injection).
 */
exports.sanitizeForLog = function (value) {
    if (typeof value !== 'string') {
        return '';
    }
    // eslint-disable-next-line no-control-regex
    var stripped = value.replace(/[\u0000-\u001f\u007f]/g, ' ');
    return stripped.replace(/\s+/g, ' ').trim().slice(0, 200);
};
/**
 * Truncate a user-controlled string for safe display in confirm dialogs
 * and other size-constrained chrome so a hostile / malformed name cannot
 * push interactive elements off-screen.
 */
exports.truncateForDisplay = function (value, maxLength) {
    if (maxLength === void 0) { maxLength = 80; }
    if (typeof value !== 'string') {
        return '';
    }
    return value.length > maxLength ? value.slice(0, maxLength) + "\u2026" : value;
};
/**
 * Stable JSON serialization (sorted object keys) for use in change
 * detection. Plain `JSON.stringify` is non-deterministic across engines
 * for object key order, which can produce phantom "unsaved changes" after
 * a server round-trip and cause the live-editing presence signal to fire
 * spuriously. Recursive; arrays are preserved in order.
 */
exports.stableStringify = function (value) {
    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value);
    }
    if (Array.isArray(value)) {
        return "[" + value.map(exports.stableStringify).join(',') + "]";
    }
    var obj = value;
    var keys = Object.keys(obj).sort();
    return "{" + keys.map(function (k) { return JSON.stringify(k) + ":" + exports.stableStringify(obj[k]); }).join(',') + "}";
};
// ─── Runtime shape validators ─────────────────────────────────────
//
// API responses must be screened at the editor boundary so a malformed
// or hostile payload cannot reach component state. Pairs with the
// prototype-pollution helpers above (a `node.id === "__proto__"` payload
// is rejected here before it can ever hit `safeWriteEdge`).
/** Type guard: value is a {@link StrategyNode} (with optional `edges`). */
exports.isStrategyNode = function (value) {
    if (!value || typeof value !== 'object')
        return false;
    var candidate = value;
    if (typeof candidate.id !== 'string' || typeof candidate.name !== 'string') {
        return false;
    }
    var edges = candidate.edges;
    return (edges === undefined || edges === null || (typeof edges === 'object' && !Array.isArray(edges)));
};
/** Type guard: value is a {@link StrategyColumn} with valid nodes. */
exports.isStrategyColumn = function (value) {
    if (!value || typeof value !== 'object')
        return false;
    var candidate = value;
    if (typeof candidate.id !== 'string' || typeof candidate.name !== 'string') {
        return false;
    }
    if (!Array.isArray(candidate.nodes))
        return false;
    return candidate.nodes.every(exports.isStrategyNode);
};
/** Type guard: value is an array of {@link StrategyColumn}. */
exports.isStrategyColumnArray = function (value) {
    return Array.isArray(value) && value.every(exports.isStrategyColumn);
};
/** Type guard: value is a {@link Strategy} record. */
exports.isStrategy = function (value) {
    if (!value || typeof value !== 'object')
        return false;
    var candidate = value;
    return (typeof candidate.id === 'number' &&
        Number.isFinite(candidate.id) &&
        typeof candidate.name === 'string' &&
        exports.isStrategyColumnArray(candidate.structure));
};
/**
 * Type guard: value is a {@link StrategiesConfigurationResponse}. Only
 * `strategies` is validated structurally — `changeHistory` and `createdAt`
 * are opaque to the editor.
 */
exports.isStrategiesConfigurationResponse = function (value) {
    if (!value || typeof value !== 'object')
        return false;
    var candidate = value;
    return Array.isArray(candidate.strategies) && candidate.strategies.every(exports.isStrategy);
};
/** Wrap a user-supplied name in single quotes after stripping log-injection chars. */
var q = function (name) { return "'" + exports.sanitizeForLog(name) + "'"; };
/**
 * Builds a change summary describing all structural and metadata changes
 * between the current editing state and the last-saved server state.
 *
 * All user-supplied names are passed through {@link sanitizeForLog} before
 * being interpolated into the free-text summary so a name containing CRLF,
 * ANSI escapes or control characters cannot smuggle fake entries into
 * downstream log parsers (CWE-117).
 *
 * Returns null when there are no changes to record.
 */
exports.buildSaveChangeHistory = function (columns, savedColumns, pendingStrategiesList, strategiesList, selectedStrategyId, userEmail) {
    var _a, _b;
    // ── Strategy metadata changes ─────────────────────────────────────
    var metaChanges = [];
    pendingStrategiesList
        .filter(function (p) { return !strategiesList.find(function (s) { return s.id === p.id; }); })
        .forEach(function (s) { return metaChanges.push("added strategy " + q(s.name)); });
    strategiesList
        .filter(function (s) { return !pendingStrategiesList.find(function (p) { return p.id === s.id; }); })
        .forEach(function (s) { return metaChanges.push("deleted strategy " + q(s.name)); });
    pendingStrategiesList.forEach(function (p) {
        var server = strategiesList.find(function (s) { return s.id === p.id; });
        if (server && server.name !== p.name) {
            metaChanges.push("renamed strategy " + q(server.name) + " to " + q(p.name));
        }
    });
    // ── Structure changes ─────────────────────────────────────────────
    var structureChanges = [];
    var allCurrentNodes = columns.flatMap(function (c) { return c.nodes || []; });
    var allSavedNodes = savedColumns.flatMap(function (c) { return c.nodes || []; });
    // Added layers (and their contents)
    columns
        .filter(function (col) { return !savedColumns.find(function (saved) { return saved.id === col.id; }); })
        .forEach(function (layer) {
        structureChanges.push("added layer " + q(layer.name));
        (layer.nodes || []).forEach(function (node) {
            structureChanges.push("added element " + q(node.name) + " in layer " + q(layer.name));
            Object.keys(node.edges || {}).forEach(function (targetId) {
                var target = allCurrentNodes.find(function (n) { return n.id === targetId; });
                if (target) {
                    structureChanges.push("added connection from " + q(node.name) + " to " + q(target.name) + " with weight " + q(node.edges[targetId]));
                }
            });
        });
    });
    // Deleted layers
    savedColumns
        .filter(function (saved) { return !columns.find(function (col) { return col.id === saved.id; }); })
        .forEach(function (layer) { return structureChanges.push("deleted layer " + q(layer.name)); });
    // Modified layers
    columns.forEach(function (col) {
        var savedCol = savedColumns.find(function (saved) { return saved.id === col.id; });
        if (!savedCol)
            return;
        if (col.name !== savedCol.name) {
            structureChanges.push("renamed layer " + q(savedCol.name) + " to " + q(col.name));
        }
        var colNodes = col.nodes || [];
        var savedNodes = savedCol.nodes || [];
        colNodes
            .filter(function (node) { return !savedNodes.find(function (saved) { return saved.id === node.id; }); })
            .forEach(function (node) {
            return structureChanges.push("added element " + q(node.name) + " in layer " + q(col.name));
        });
        savedNodes
            .filter(function (saved) { return !colNodes.find(function (node) { return node.id === saved.id; }); })
            .forEach(function (node) {
            return structureChanges.push("deleted element " + q(node.name) + " from layer " + q(col.name));
        });
        colNodes.forEach(function (node) {
            var savedNode = savedNodes.find(function (saved) { return saved.id === node.id; });
            if (!savedNode)
                return;
            if (node.name !== savedNode.name) {
                structureChanges.push("renamed element " + q(savedNode.name) + " to " + q(node.name) + " in layer " + q(col.name));
            }
            var nodeEdges = node.edges || {};
            var savedEdges = savedNode.edges || {};
            Object.keys(nodeEdges).forEach(function (targetId) {
                var currentWeight = nodeEdges[targetId];
                var savedWeight = savedEdges[targetId];
                var target = allCurrentNodes.find(function (n) { return n.id === targetId; });
                if (savedWeight === undefined) {
                    if (target) {
                        structureChanges.push("added connection from " + q(node.name) + " to " + q(target.name) + " with weight " + q(currentWeight));
                    }
                }
                else if (currentWeight !== savedWeight) {
                    if (target) {
                        structureChanges.push("changed connection weight from " + q(node.name) + " to " + q(target.name) + " from " + q(savedWeight) + " to " + q(currentWeight));
                    }
                }
            });
            Object.keys(savedEdges).forEach(function (targetId) {
                if (nodeEdges[targetId] === undefined) {
                    var target = allSavedNodes.find(function (n) { return n.id === targetId; });
                    if (target) {
                        structureChanges.push("removed connection from " + q(node.name) + " to " + q(target.name));
                    }
                }
            });
        });
    });
    // ── Build change summary ──────────────────────────────────────────
    var allChanges = __spreadArrays(metaChanges, structureChanges);
    if (allChanges.length === 0)
        return null;
    var strategyName = structureChanges.length > 0 && selectedStrategyId
        ? ((_b = (_a = pendingStrategiesList.find(function (s) { return s.id === selectedStrategyId; })) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "#" + selectedStrategyId)
        : '';
    var strategyLabel = strategyName ? exports.sanitizeForLog(strategyName) : '';
    var safeUser = exports.sanitizeForLog(userEmail);
    return "" + safeUser + (strategyLabel ? " [" + strategyLabel + "]" : '') + " " + allChanges.join(', ');
};
